import {
  Control,
  FieldErrors,
  UseFormClearErrors,
  UseFormSetValue,
} from 'react-hook-form';
import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import copy from 'copy-to-clipboard';
import IconLoader from '../../icons/loader';
import formatDate from '../../../helpers/format-date';
import SidepanelTextRow from '../sidepanel-text-row';
import SidepanelSelectRow from '../sidepanel-select-row';
import SidepanelDateRow from '../sidepanel-date-row';
import { SelectItemProps } from '../../select/types';
import {
  Account,
  Balance,
  BaseellaStatus,
  Currency,
  ExternalAccount,
  OutgoingTransaction,
  OutgoingTransactionGeneral,
  PaymentType,
} from '../types';
import ButtonPseudo from '../../button/button-pseudo';
import SidepanelTextareaRow from '../sidepanel-textarea-row';
import SidepanelAutocompleteRow from '../sidepanel-autocomplete-row';
import IconRefresh from '../../icons/refresh';
import InputText from '../../inputs/input-text';
import formatBalance from '../../../helpers/format-balance';
import SidepanelDefaultRow from '../../side-panel/side-panel-default-row';
import { Pagination, PermissionEnum } from '../../../base-types';
import SidepanelAmountRow from '../sidepanel-amount-row';
import IconCopy from '../../icons/copy-two';
import IconChecked from '../../icons/checked';
import IconClose from '../../icons/close';
import IconCalendar from '../../icons/calendar';
import CheckboxInput from '../../checkbox/checkbox';
import ExternalAccountRow from '../external-account-row';
import DisableCreationOption from '../disable-creation-option/disable-creation-options';

import styles from '../transaction.module.css';

interface OutgoingGeneralContentProps {
  general: OutgoingTransactionGeneral;
  isLoading: boolean;
  currencies?: SelectItemProps[];
  senderAccounts?: SelectItemProps[];
  pspPartnerAccounts?: SelectItemProps[];
  paymentsTypes?: SelectItemProps[];
  chargeTypes?: SelectItemProps[];
  control?: Control;
  isEditable?: boolean;
  errors?: FieldErrors<OutgoingTransaction>;
  onSearchAutocomplete?: (name: string, search: string) => void;
  pspPartnersIsLoading?: boolean;
  senderAccountsIsLoading?: boolean;
  pspPartnerAccountsIsLoading?: boolean;
  pspPartnerIsFilled?: boolean;
  paymentTypeIsFilled?: boolean;
  paymentType?: PaymentType;
  pspPartners?: SelectItemProps[];
  baseellaStatus?: BaseellaStatus;
  feeText?: string;
  accountBalance?: number;
  currencyId?: string;
  balanceIsLoading?: boolean;
  updateBalance?: () => void;
  currencyName?: string;
  pspPartnerName?: string;
  updatePspPartnerBalance?: () => void;
  pspPartnerBalanceIsLoading?: boolean;
  pspPartnerAccountBalance?: number;
  withTransactionUrl?: boolean;
  transactionUrlType?: string;
  transactionId?: string;
  activePermission?: PermissionEnum[];
  amount?: string;
  setAmount?: (data?: string) => void;
  balances?: Balance[];
  accountBalancesIsLoading?: boolean;
  currenciesList?: Currency[];
  fields?: any;
  overdraftEnabled?: boolean;
  senderAccountsWithInfo?: Account[];
  pspPartnersAccountsList?: any[];
  pspPartnerAccountBalanceInfo?: Balance;
  pspPartnerAccountsNextPageIsLoading?: boolean;
  pspPartnerAccountsOnScroll?: () => void;
  pspPartnerAccountsPagination?: Pagination;
  externalAccounts?: ExternalAccount[];
  externalAccountsIsLoading?: boolean;
  updateExternalBalance?: () => void;
  onSearchExternalAccounts?: (data: string) => void;
  onClearExternalAccounts?: () => void;
  onGetExternalAccountsListAction?: (data: {
    search?: string;
    cursor?: string;
  }) => void;
  externalAccountsPagination?: Pagination;
  externalAccountsNextPageIsLoading?: boolean;
  setValue?: UseFormSetValue<any>;
  clearErrors?: UseFormClearErrors<any>;
}

export default function OutgoingGeneralContent({
  general,
  isLoading,
  currencies,
  senderAccounts,
  pspPartnerAccounts,
  paymentsTypes,
  chargeTypes,
  control,
  isEditable,
  errors,
  onSearchAutocomplete,
  pspPartnersIsLoading,
  senderAccountsIsLoading,
  pspPartnerAccountsIsLoading,
  pspPartnerIsFilled,
  paymentTypeIsFilled,
  pspPartners,
  baseellaStatus,
  feeText,
  paymentType,
  accountBalance,
  currencyId,
  balanceIsLoading,
  updateBalance,
  currencyName,
  pspPartnerName,
  updatePspPartnerBalance,
  pspPartnerAccountBalance,
  pspPartnerBalanceIsLoading,
  withTransactionUrl,
  transactionUrlType,
  transactionId,
  activePermission,
  amount,
  setAmount,
  balances,
  accountBalancesIsLoading,
  currenciesList,
  fields,
  overdraftEnabled,
  senderAccountsWithInfo,
  pspPartnersAccountsList,
  pspPartnerAccountBalanceInfo,
  pspPartnerAccountsNextPageIsLoading,
  pspPartnerAccountsOnScroll,
  pspPartnerAccountsPagination,
  externalAccounts,
  externalAccountsIsLoading,
  updateExternalBalance,
  onSearchExternalAccounts,
  onClearExternalAccounts,
  onGetExternalAccountsListAction,
  externalAccountsPagination,
  externalAccountsNextPageIsLoading,
  setValue,
  clearErrors,
}: OutgoingGeneralContentProps): React.JSX.Element {
  const [copyIsSuccess, setCopyIsSuccess] = useState(false);
  const [infoValue, setInfoValue] = useState('');
  const [accountType, setAccountType] = useState<'internal' | 'external'>();
  const [selectedExternalAccount, setSelelectedExternalAccount] = useState<
    ExternalAccount | undefined
  >(undefined);

  const onCopy = (copyValue: string) => {
    setCopyIsSuccess(true);
    copy(copyValue);

    setTimeout(() => {
      setCopyIsSuccess(false);
    }, 2000);
  };

  useEffect(() => {
    if (general && general.pspIntegrationAccountId && !accountType) {
      setAccountType('external');

      if (externalAccounts) {
        const currentAccount = externalAccounts?.find(
          (item) => item.id === general.pspIntegrationAccountId,
        );

        if (currentAccount) {
          setSelelectedExternalAccount(currentAccount);
        }
      }
    }
  }, [general, externalAccounts, accountType]);

  useEffect(() => {
    if (!general && !transactionId && !accountType) {
      setAccountType('internal');
    }
  }, [general, transactionId, accountType]);

  useEffect(() => {
    if (general && general.info) {
      setInfoValue(general.info);
    }
  }, [general]);

  useEffect(() => {
    if (
      general &&
      !general.pspIntegrationAccountId &&
      general.senderAccountNumber &&
      senderAccounts?.find(
        (item) => item.value === general.senderAccountNumber,
      ) &&
      !accountType
    ) {
      setAccountType('internal');
    }
  }, [general, accountType, senderAccounts]);

  let content;
  // eslint-disable-next-line no-useless-assignment
  let availableCurrencies: SelectItemProps[] = [];
  let currentSenderAccount;

  const enableEdit = Boolean(control);

  if (selectedExternalAccount) {
    availableCurrencies =
      selectedExternalAccount.pspIntegrationPayment?.currencies?.map(
        (item) => ({
          name: item.currency.name,
          value: item.currency.id,
        }),
      ) as SelectItemProps[];
  } else {
    availableCurrencies = currencies as SelectItemProps[];

    if (
      !senderAccounts?.find(
        (item) => item.value === fields?.senderAccountNumber,
      )
    ) {
      availableCurrencies = currenciesList?.map((item) => ({
        name: item.name,
        value: item.id,
        suffix: item.description,
      })) as SelectItemProps[];
    }
  }

  if (accountType !== 'external') {
    currentSenderAccount = senderAccountsWithInfo?.find(
      (item) =>
        item.fullNumber === fields?.senderAccountNumber ||
        item.fullNumber === general?.senderAccountNumber,
    );
  } else {
    currentSenderAccount = externalAccounts?.find(
      (item) =>
        item.account.fullNumber === fields?.senderAccountNumber ||
        item.account.fullNumber === general?.senderAccountNumber,
    )?.account;
  }

  if (isLoading) {
    content = (
      <div className={styles.loader}>
        <IconLoader className={styles.loaderIcon} />
      </div>
    );
  } else {
    let currencyValue = '';
    let pspPartnerValue = '';

    if (!control) {
      currencyValue = currencyName as string;
      pspPartnerValue = pspPartnerName as string;
    } else if (enableEdit) {
      currencyValue = general?.currencyId;
      pspPartnerValue = general?.pspPartnerId as string;
    }

    const currentBalance = balances?.find(
      (item) =>
        item.currencyId === fields?.currencyId ||
        item.currencyId === general?.currencyId,
    );

    const currentPspAccount = pspPartnersAccountsList?.find(
      (item) =>
        item.account.fullNumber === general?.pspPartnerAccountNumber ||
        item?.account?.fullNumber === fields?.pspPartnerAccountNumber,
    );

    content = (
      <>
        <div className={styles.transactionBlockWrapper}>
          <div className={styles.transactionTitle}>General Info</div>
          {general && (
            <SidepanelDateRow
              title="Creation Date"
              value={enableEdit ? '' : formatDate(general?.createdAt || '')}
              copyValue={formatDate(
                enableEdit ? fields?.createdAt : general?.createdAt || '',
              )}
              name="createdAt"
              placeholder="Select Creation Date"
              control={control}
              enableEdit={enableEdit}
              copyEnabled={Boolean(general)}
              disabled
              tabIndex={-1}
              clearErrors={clearErrors}
            />
          )}
          <SidepanelDateRow
            title="Transfer Date"
            value={enableEdit ? '' : formatDate(general?.transferDate || '')}
            copyValue={formatDate(
              enableEdit ? fields?.transferDate : general?.transferDate || '',
            )}
            name="transferDate"
            placeholder="Select Transfer Date"
            control={control}
            enableEdit={enableEdit}
            disabled={!isEditable || Boolean(transactionId && general)}
            tabIndex={!isEditable || Boolean(transactionId && general) ? -1 : 0}
            error={errors?.transferDate?.message}
            copyEnabled={Boolean(general)}
            clearErrors={clearErrors}
          />
          <SidepanelDateRow
            title="Accepted Out"
            value={enableEdit ? '' : formatDate(general?.acceptedOut || '')}
            copyValue={formatDate(
              enableEdit ? fields?.acceptedOut : general?.acceptedOut || '',
            )}
            name="acceptedOut"
            placeholder="Select Accepted Out"
            control={control}
            enableEdit={enableEdit}
            disabled={!isEditable || baseellaStatus !== BaseellaStatus.SENT}
            tabIndex={
              !isEditable || baseellaStatus !== BaseellaStatus.SENT ? -1 : 0
            }
            error={errors?.acceptedOut?.message}
            copyEnabled={Boolean(general)}
            clearErrors={clearErrors}
          />
          <div className={styles.accountsTypesSelectorWrapper}>
            <div className={styles.accountsTypesSelector}>
              <div className={styles.accountType}>
                <CheckboxInput
                  type="radio"
                  name="internal"
                  checked={accountType === 'internal'}
                  title="Internal"
                  disabled={
                    !isEditable ||
                    Boolean(
                      general &&
                      baseellaStatus !== BaseellaStatus.UNDEFINED_FUNDS &&
                      senderAccounts?.find(
                        (item) => item.value === fields?.senderAccountNumber,
                      ),
                    )
                  }
                  onChange={() => {
                    setAccountType('internal');
                    setSelelectedExternalAccount(undefined);

                    if (setValue && !transactionId) {
                      setValue('pspIntegrationAccountId', '');
                    }
                  }}
                />
              </div>
              <div className={styles.accountType}>
                <CheckboxInput
                  type="radio"
                  name="external"
                  checked={accountType === 'external'}
                  title="External"
                  disabled={
                    !isEditable ||
                    Boolean(
                      general &&
                      baseellaStatus !== BaseellaStatus.UNDEFINED_FUNDS &&
                      senderAccounts?.find(
                        (item) => item.value === fields?.senderAccountNumber,
                      ),
                    )
                  }
                  onChange={() => {
                    setAccountType('external');

                    if (
                      accountType !== 'external' &&
                      !fields.pspIntegrationAccountId &&
                      onSearchExternalAccounts
                    ) {
                      onSearchExternalAccounts('');
                    }

                    if (setValue && !transactionId) {
                      setValue('senderAccountNumber', '');
                    }
                  }}
                />
              </div>
            </div>
            {accountType === 'internal' && (
              <SidepanelAutocompleteRow
                title="Sender Account"
                value={enableEdit ? '' : general?.senderAccountNumber}
                copyValue={
                  enableEdit
                    ? fields?.senderAccountNumber
                    : general?.senderAccountNumber
                }
                name="senderAccountNumber"
                placeholder="Choose Sender Account"
                autocompleteList={senderAccounts || []}
                isLoading={senderAccountsIsLoading}
                control={control}
                enableEdit={enableEdit}
                disabled={!isEditable || Boolean(transactionId && general)}
                tabIndex={
                  !isEditable || Boolean(transactionId && general) ? -1 : 0
                }
                error={errors?.senderAccountNumber?.message}
                onSearchAutocomplete={onSearchAutocomplete}
                copyEnabled={Boolean(transactionId && general)}
                clearErrors={clearErrors}
                withSuffix
              />
            )}
            {accountType === 'external' &&
              activePermission?.includes(
                PermissionEnum.PSP_INTEGRATION_ACCOUNT_LIST,
              ) && (
                <ExternalAccountRow
                  title="External Beneficiary Account"
                  value={enableEdit ? '' : general?.pspIntegrationAccountId}
                  name="pspIntegrationAccountId"
                  placeholder="Choose External Beneficiary Account"
                  control={control}
                  enableEdit={enableEdit}
                  tabIndex={
                    !isEditable ||
                    Boolean(
                      general &&
                      baseellaStatus !== BaseellaStatus.UNDEFINED_FUNDS &&
                      senderAccounts?.find(
                        (item) => item.value === fields?.senderAccountNumber,
                      ),
                    )
                      ? -1
                      : 0
                  }
                  disabled={
                    !isEditable ||
                    Boolean(
                      general &&
                      baseellaStatus !== BaseellaStatus.UNDEFINED_FUNDS &&
                      senderAccounts?.find(
                        (item) => item.value === fields?.senderAccountNumber,
                      ),
                    )
                  }
                  error={errors?.pspIntegrationAccountId?.message}
                  copyEnabled={Boolean(general)}
                  copyValue={
                    externalAccounts?.find(
                      (item) =>
                        item.id === fields.pspInegrationAccountId ||
                        item.id === general?.pspIntegrationAccountId,
                    )?.iban
                  }
                  externalAccounts={externalAccounts}
                  setValue={setValue}
                  isLoading={externalAccountsIsLoading}
                  selectedExternalAccount={selectedExternalAccount}
                  setSelelectedExternalAccount={setSelelectedExternalAccount}
                  pspIntegrationAccountId={fields?.pspIntegrationAccountId}
                  onSearch={onSearchExternalAccounts}
                  onClear={onClearExternalAccounts}
                  onGetList={onGetExternalAccountsListAction}
                  pagination={externalAccountsPagination}
                  transactionId={transactionId}
                  overdraftEnabled={overdraftEnabled}
                  externalAccountsNextPageIsLoading={
                    externalAccountsNextPageIsLoading
                  }
                  clearErrors={clearErrors}
                  isOutgoing
                />
              )}
            {accountType === 'external' &&
              !activePermission?.includes(
                PermissionEnum.PSP_INTEGRATION_ACCOUNT_LIST,
              ) && (
                <div className={styles.noAccess}>
                  You don&apos;t have access to this section
                </div>
              )}
          </div>
          <div
            className={`${styles.amountRow} ${!control ? styles.noFlex : ''}`}
          >
            <div className={styles.amountRowItem}>
              <SidepanelAmountRow
                title="Amount"
                value={enableEdit ? '' : general?.sum.toFixed(2)}
                amount={amount}
                copyValue={enableEdit ? fields?.sum : general?.sum}
                setAmount={setAmount}
                name="sum"
                placeholder="Enter amount"
                control={control}
                enableEdit={enableEdit}
                disabled={
                  !isEditable ||
                  Boolean(transactionId && general) ||
                  !balances ||
                  (balances && !balances.length)
                }
                tabIndex={
                  !isEditable ||
                  Boolean(transactionId && general) ||
                  !balances ||
                  (balances && !balances.length)
                    ? -1
                    : 0
                }
                error={errors?.sum?.message}
                currenciesList={currenciesList || []}
                currencyId={currencyId}
                copyEnabled={Boolean(transactionId && general)}
                clearErrors={clearErrors}
              />
            </div>
            <div className={styles.amountRowItem}>
              <SidepanelSelectRow
                title="Currency"
                value={currencyValue}
                name="currencyId"
                placeholder="Choose Currency"
                selectList={availableCurrencies || []}
                control={control}
                enableEdit={enableEdit}
                disabled={
                  !isEditable ||
                  Boolean(transactionId && general) ||
                  !balances ||
                  (balances && !balances.length)
                }
                tabIndex={
                  !isEditable ||
                  Boolean(transactionId && general) ||
                  !balances ||
                  (balances && !balances.length)
                    ? -1
                    : 0
                }
                error={errors?.currencyId?.message}
                copyValue={
                  enableEdit
                    ? availableCurrencies?.find(
                        (item) => item.value === currencyValue,
                      )?.name
                    : general?.currency?.name
                }
                copyEnabled={Boolean(transactionId && general)}
                clearErrors={clearErrors}
              />
            </div>
          </div>
          {!accountBalancesIsLoading && balances && !balances?.length && (
            <div className={`${styles.fee} ${styles.noCurrencies}`}>
              Account doesn&apos;t have available currencies
            </div>
          )}
          {accountBalancesIsLoading && (
            <div className={`${styles.fee} ${styles.noCurrencies}`}>
              Currencies and balances is loading...
            </div>
          )}
          {control && (
            <>
              {(!overdraftEnabled ||
                (currentSenderAccount &&
                  (!currentSenderAccount?.overdraftEnabled ||
                    !currentBalance?.overdraftEnabled))) && (
                <div
                  className={`${styles.balanceWrapper} ${
                    general ? styles.balanceWrapperWithCopy : ''
                  }`}
                >
                  <InputText
                    disabled
                    tabIndex={-1}
                    name="balance"
                    placeholder=""
                    title="Current balance"
                    value={
                      accountBalance !== undefined
                        ? `${formatBalance(
                            accountBalance,
                            currenciesList?.find(
                              (item) => item.id === currencyId,
                            ),
                          )} ${
                            availableCurrencies?.find(
                              (item) => item.value === currencyId,
                            )?.name
                          }`
                        : '-'
                    }
                  />
                  <ButtonPseudo
                    icon={<IconRefresh />}
                    onClick={() => {
                      if (selectedExternalAccount && updateExternalBalance) {
                        updateExternalBalance();
                      } else if (!selectedExternalAccount && updateBalance) {
                        updateBalance();
                      }
                    }}
                    className={`${styles.updateBalanceButton} ${
                      balanceIsLoading ? styles.loading : ''
                    } `}
                    disabled={accountBalance === undefined || !isEditable}
                    tabIndex={-1}
                  />
                  {Boolean(general) && (
                    <button
                      type="button"
                      className={`${styles.copyButton} ${
                        styles.autocompleteCopyButton
                      } ${copyIsSuccess ? styles.success : ''}`}
                      onClick={() =>
                        onCopy(
                          accountBalance !== undefined
                            ? accountBalance?.toString()
                            : '-',
                        )
                      }
                      aria-label="Copy"
                    >
                      <IconCopy />
                    </button>
                  )}
                </div>
              )}
              {overdraftEnabled &&
                currentSenderAccount?.overdraftEnabled &&
                currentBalance?.overdraftEnabled && (
                  <div className={styles.overdraftBalanceWrapper}>
                    <div
                      className={`${styles.overdraftBalanceValue} ${
                        general ? styles.withCopy : ''
                      }`}
                    >
                      <InputText
                        disabled
                        tabIndex={-1}
                        name="balance"
                        placeholder=""
                        title="Total available balance"
                        className={styles.overdraftBalance}
                        value={
                          accountBalance !== undefined
                            ? `${formatBalance(
                                accountBalance,
                                currenciesList?.find(
                                  (item) => item.id === currencyId,
                                ),
                              )} ${
                                currencies?.find(
                                  (item) => item.value === currencyId,
                                )?.name
                              }`
                            : '-'
                        }
                      />
                      <ButtonPseudo
                        icon={<IconRefresh />}
                        onClick={() => {
                          if (
                            selectedExternalAccount &&
                            updateExternalBalance
                          ) {
                            updateExternalBalance();
                          } else if (
                            !selectedExternalAccount &&
                            updateBalance
                          ) {
                            updateBalance();
                          }
                        }}
                        className={`${styles.updateBalanceButton} ${
                          balanceIsLoading ? styles.loading : ''
                        } `}
                        disabled={accountBalance === undefined || !isEditable}
                        tabIndex={-1}
                      />
                      {Boolean(general) && (
                        <button
                          type="button"
                          className={`${styles.copyButton} ${
                            styles.autocompleteCopyButton
                          } ${copyIsSuccess ? styles.success : ''}`}
                          onClick={() =>
                            onCopy(
                              accountBalance !== undefined
                                ? accountBalance?.toString()
                                : '-',
                            )
                          }
                          aria-label="Copy"
                        >
                          <IconCopy />
                        </button>
                      )}
                    </div>
                    <div className={styles.overdraftBalanceValue}>
                      <InputText
                        disabled
                        tabIndex={-1}
                        name="availableOverdraft"
                        placeholder=""
                        title="Remaining overdraft"
                        value={
                          currentBalance && currentBalance?.overdraftEnabled
                            ? `${formatBalance(
                                // eslint-disable-next-line no-nested-ternary
                                currentBalance?.value < 0
                                  ? (currentBalance?.overdraftValue as number) -
                                      Math.abs(currentBalance?.value) <
                                    0
                                    ? 0
                                    : (currentBalance?.overdraftValue as number) -
                                      Math.abs(currentBalance?.value)
                                  : (currentBalance?.overdraftValue as number),
                                currenciesList?.find(
                                  (item) => item.id === currencyId,
                                ),
                              )} ${
                                currencies?.find(
                                  (item) => item.value === currencyId,
                                )?.name
                              }`
                            : '-'
                        }
                      />
                    </div>
                    <div className={styles.overdraftBalanceValue}>
                      <InputText
                        disabled
                        tabIndex={-1}
                        name="overdraftLimit"
                        placeholder=""
                        title="Overdraft limit"
                        value={
                          currentBalance && currentBalance?.overdraftEnabled
                            ? `${formatBalance(
                                currentBalance?.overdraftValue as number,
                                currenciesList?.find(
                                  (item) => item.id === currencyId,
                                ),
                              )} ${
                                currencies?.find(
                                  (item) => item.value === currencyId,
                                )?.name
                              }`
                            : '-'
                        }
                      />
                    </div>
                  </div>
                )}
            </>
          )}
          {!control && (
            <>
              {(!overdraftEnabled ||
                (currentSenderAccount &&
                  (!currentSenderAccount?.overdraftEnabled ||
                    !currentBalance?.overdraftEnabled))) && (
                <SidepanelDefaultRow
                  title="Current balance"
                  valueClassName={general ? styles.readCopyButtonWrapper : ''}
                >
                  {accountBalance !== undefined
                    ? `${formatBalance(
                        accountBalance,
                        currenciesList?.find((item) => item.id === currencyId),
                      )} ${general?.currency?.name}`
                    : '-'}
                  {Boolean(general) && (
                    <button
                      type="button"
                      className={`${styles.readCopyButton} ${
                        copyIsSuccess ? styles.success : ''
                      }`}
                      onClick={() =>
                        onCopy(
                          accountBalance !== undefined
                            ? accountBalance?.toString()
                            : '-',
                        )
                      }
                      aria-label="Copy"
                    >
                      <IconCopy />
                    </button>
                  )}
                </SidepanelDefaultRow>
              )}
              {overdraftEnabled &&
                currentSenderAccount?.overdraftEnabled &&
                currentBalance?.overdraftEnabled && (
                  <>
                    <SidepanelDefaultRow
                      title="Total available balance"
                      valueClassName={
                        general ? styles.readCopyButtonWrapper : ''
                      }
                    >
                      {accountBalance !== undefined
                        ? `${formatBalance(
                            accountBalance,
                            currenciesList?.find(
                              (item) => item.id === currencyId,
                            ),
                          )} ${general?.currency?.name}`
                        : '-'}
                      {Boolean(general) && (
                        <button
                          type="button"
                          className={`${styles.readCopyButton} ${
                            copyIsSuccess ? styles.success : ''
                          }`}
                          onClick={() =>
                            onCopy(
                              accountBalance !== undefined
                                ? accountBalance?.toString()
                                : '-',
                            )
                          }
                          aria-label="Copy"
                        >
                          <IconCopy />
                        </button>
                      )}
                    </SidepanelDefaultRow>
                    <SidepanelDefaultRow title="Remaining overdraft">
                      {currentBalance && currentBalance?.overdraftEnabled
                        ? `${formatBalance(
                            // eslint-disable-next-line no-nested-ternary
                            currentBalance?.value < 0
                              ? (currentBalance?.overdraftValue as number) -
                                  Math.abs(currentBalance?.value) <
                                0
                                ? 0
                                : (currentBalance?.overdraftValue as number) -
                                  Math.abs(currentBalance?.value)
                              : (currentBalance?.overdraftValue as number),
                            currenciesList?.find(
                              (item) => item.id === currencyId,
                            ),
                          )} ${
                            currencies?.find(
                              (item) => item.value === currencyId,
                            )?.name
                          }`
                        : '-'}
                    </SidepanelDefaultRow>
                    <SidepanelDefaultRow title="Overdraft Limit">
                      {currentBalance && currentBalance?.overdraftEnabled
                        ? `${formatBalance(
                            currentBalance?.overdraftValue as number,
                            currenciesList?.find(
                              (item) => item.id === currencyId,
                            ),
                          )} ${
                            currencies?.find(
                              (item) => item.value === currencyId,
                            )?.name
                          }`
                        : '-'}
                    </SidepanelDefaultRow>
                  </>
                )}
            </>
          )}
          {baseellaStatus === BaseellaStatus.REFUNDED &&
            general.refundAmount?.toString() &&
            general?.refundAmount !== null &&
            general?.refundAmount !== undefined && (
              <div className={styles.refundWrapper}>
                <div className={styles.refundTop}>
                  <div className={styles.refundTopItem}>
                    <div className={styles.refundTopItemTitle}>Refund Date</div>
                    <div className={styles.refundTopItemValue}>
                      <div className={styles.refundTopItemValueFlex}>
                        {formatDate(general.refundedAt as string)}
                        <IconCalendar />
                      </div>
                    </div>
                  </div>
                  <div className={styles.refundTopItem}>
                    <div className={styles.refundTopItemTitle}>
                      Refund Amount
                    </div>
                    <div className={styles.refundTopItemValue}>
                      {formatBalance(general.refundAmount, general.currency)}
                    </div>
                  </div>
                </div>
                <div className={styles.feeRefunded}>
                  {general?.refundFees ? (
                    <IconChecked
                      width={16}
                      height={16}
                      className={styles.checkedIcon}
                    />
                  ) : (
                    <IconClose width={16} height={16} />
                  )}
                  Fee Refunded
                </div>
              </div>
            )}
          {feeText && !transactionId && (
            <div className={styles.fee}>{feeText}</div>
          )}
        </div>
        <div className={styles.transactionBlockWrapper}>
          <div className={styles.transactionTitle}>Details</div>
          <SidepanelSelectRow
            title="Payment type"
            value={
              enableEdit
                ? ''
                : paymentsTypes?.find(
                    (item) => item.value === general?.paymentType,
                  )?.name
            }
            name="paymentType"
            placeholder="Choose Payment Type"
            selectList={paymentsTypes || []}
            control={control}
            enableEdit={enableEdit}
            disabled={
              !isEditable ||
              (Boolean(transactionId && general) &&
                baseellaStatus !== BaseellaStatus.READY_TO_PSP) ||
              !currencyId
            }
            tabIndex={
              !isEditable ||
              (Boolean(transactionId && general) &&
                baseellaStatus !== BaseellaStatus.READY_TO_PSP) ||
              !currencyId
                ? -1
                : 0
            }
            error={errors?.paymentType?.message}
            copyValue={
              enableEdit
                ? paymentsTypes?.find(
                    (item) => item.value === fields?.paymentType,
                  )?.name
                : paymentsTypes?.find(
                    (item) => item.value === general?.paymentType,
                  )?.name
            }
            copyEnabled={Boolean(transactionId && general)}
            clearErrors={clearErrors}
            withClearButton
          />
          <SidepanelSelectRow
            title="Charges Type"
            value={enableEdit ? '' : general?.chargeType}
            name="chargeType"
            placeholder="Choose Charge Type"
            control={control}
            selectList={chargeTypes || []}
            enableEdit={enableEdit}
            disabled={
              !isEditable ||
              (Boolean(transactionId && general) &&
                baseellaStatus !== BaseellaStatus.READY_TO_PSP) ||
              paymentType !== PaymentType.SWIFT
            }
            tabIndex={
              !isEditable ||
              (Boolean(transactionId && general) &&
                baseellaStatus !== BaseellaStatus.READY_TO_PSP) ||
              paymentType !== PaymentType.SWIFT
                ? -1
                : 0
            }
            error={errors?.chargeType?.message}
            copyValue={
              enableEdit
                ? chargeTypes?.find((item) => item.value === fields?.chargeType)
                    ?.name
                : general?.chargeType
            }
            copyEnabled={Boolean(transactionId && general)}
            clearErrors={clearErrors}
          />
          <SidepanelAutocompleteRow
            title="Partner Bank"
            value={pspPartnerValue}
            name="pspPartnerId"
            placeholder="Choose Partner Bank"
            autocompleteList={pspPartners || []}
            isLoading={pspPartnersIsLoading}
            control={control}
            enableEdit={enableEdit}
            disabled={
              !isEditable ||
              !paymentTypeIsFilled ||
              (Boolean(transactionId && general) &&
                baseellaStatus !== BaseellaStatus.READY_TO_PSP)
            }
            tabIndex={
              !isEditable ||
              !paymentTypeIsFilled ||
              (Boolean(transactionId && general) &&
                baseellaStatus !== BaseellaStatus.READY_TO_PSP)
                ? -1
                : 0
            }
            copyValue={
              enableEdit
                ? pspPartners?.find((item) => item.value === pspPartnerValue)
                    ?.name
                : pspPartnerValue
            }
            copyEnabled={Boolean(transactionId && general)}
            onSearchAutocomplete={onSearchAutocomplete}
            error={errors?.pspPartnerId?.message}
            clearErrors={clearErrors}
            withClearButton
          />
          <SidepanelAutocompleteRow
            title="Partner Bank Account"
            value={enableEdit ? '' : general?.pspPartnerAccountNumber}
            name="pspPartnerAccountNumber"
            placeholder="Choose Partner Bank Account"
            autocompleteList={pspPartnerAccounts || []}
            isLoading={pspPartnerAccountsIsLoading}
            control={control}
            enableEdit={enableEdit}
            disabled={
              !isEditable ||
              !pspPartnerIsFilled ||
              (Boolean(transactionId && general) &&
                baseellaStatus !== BaseellaStatus.READY_TO_PSP) ||
              !activePermission?.includes(PermissionEnum.PSP_LIST)
            }
            tabIndex={
              !isEditable ||
              !pspPartnerIsFilled ||
              !activePermission?.includes(PermissionEnum.PSP_LIST) ||
              (Boolean(transactionId && general) &&
                baseellaStatus !== BaseellaStatus.READY_TO_PSP)
                ? -1
                : 0
            }
            error={
              errors?.pspPartnerAccountNumber?.message ||
              (!activePermission?.includes(PermissionEnum.PSP_LIST)
                ? 'Data not loaded. Permission denied'
                : '')
            }
            onSearchAutocomplete={onSearchAutocomplete}
            copyValue={
              enableEdit
                ? fields?.pspPartnerAccountNumber
                : general?.pspPartnerAccountNumber
            }
            copyEnabled={Boolean(transactionId && general)}
            pagination={pspPartnerAccountsPagination}
            onScroll={pspPartnerAccountsOnScroll}
            nextPageIsLoading={pspPartnerAccountsNextPageIsLoading}
            clearErrors={clearErrors}
            withInfiniteScroll
            withSuffix
          />
          {control && (
            <>
              {(!overdraftEnabled ||
                (currentPspAccount &&
                  (!currentPspAccount?.account?.overdraftEnabled ||
                    !pspPartnerAccountBalanceInfo?.overdraftEnabled))) && (
                <div
                  className={`${styles.balanceWrapper} ${styles.moreMargin} ${
                    general ? styles.balanceWrapperWithCopy : ''
                  }`}
                >
                  <InputText
                    disabled
                    tabIndex={-1}
                    name="balance"
                    placeholder=""
                    title="Current balance"
                    value={
                      pspPartnerAccountBalance !== undefined
                        ? `${formatBalance(pspPartnerAccountBalance)} ${
                            availableCurrencies?.find(
                              (item) => item.value === currencyId,
                            )?.name
                          }`
                        : '-'
                    }
                  />
                  <ButtonPseudo
                    icon={<IconRefresh />}
                    onClick={() =>
                      updatePspPartnerBalance && updatePspPartnerBalance()
                    }
                    className={`${styles.updateBalanceButton} ${
                      pspPartnerBalanceIsLoading ? styles.loading : ''
                    } `}
                    disabled={
                      pspPartnerAccountBalance === undefined || !isEditable
                    }
                    tabIndex={-1}
                  />
                  {Boolean(general) && (
                    <button
                      type="button"
                      className={`${styles.copyButton} ${
                        styles.autocompleteCopyButton
                      } ${copyIsSuccess ? styles.success : ''}`}
                      onClick={() =>
                        onCopy(
                          pspPartnerAccountBalance !== undefined
                            ? pspPartnerAccountBalance?.toString()
                            : '-',
                        )
                      }
                      aria-label="Copy"
                    >
                      <IconCopy />
                    </button>
                  )}
                </div>
              )}
              {overdraftEnabled &&
                currentPspAccount?.account?.overdraftEnabled &&
                pspPartnerAccountBalanceInfo?.overdraftEnabled && (
                  <div
                    className={`${styles.overdraftBalanceWrapper} ${styles.withMargin}`}
                  >
                    <div
                      className={`${styles.overdraftBalanceValue} ${
                        general ? styles.withCopy : ''
                      }`}
                    >
                      <InputText
                        disabled
                        tabIndex={-1}
                        name="pspPartnerBalance"
                        placeholder=""
                        title="Total available balance"
                        className={styles.overdraftBalance}
                        value={
                          pspPartnerAccountBalance !== undefined
                            ? `${formatBalance(
                                pspPartnerAccountBalance,
                                currenciesList?.find(
                                  (item) => item.id === currencyId,
                                ),
                              )} ${
                                availableCurrencies?.find(
                                  (item) => item.value === currencyId,
                                )?.name
                              }`
                            : '-'
                        }
                      />
                      <ButtonPseudo
                        icon={<IconRefresh />}
                        onClick={() =>
                          updatePspPartnerBalance && updatePspPartnerBalance()
                        }
                        className={`${styles.updateBalanceButton} ${
                          balanceIsLoading ? styles.loading : ''
                        } `}
                        disabled={
                          pspPartnerAccountBalance === undefined || !isEditable
                        }
                        tabIndex={-1}
                      />
                      {Boolean(general) && (
                        <button
                          type="button"
                          className={`${styles.copyButton} ${
                            styles.autocompleteCopyButton
                          } ${copyIsSuccess ? styles.success : ''}`}
                          onClick={() =>
                            onCopy(
                              pspPartnerAccountBalance !== undefined
                                ? pspPartnerAccountBalance?.toString()
                                : '-',
                            )
                          }
                          aria-label="Copy"
                        >
                          <IconCopy />
                        </button>
                      )}
                    </div>
                    <div className={styles.overdraftBalanceValue}>
                      <InputText
                        disabled
                        tabIndex={-1}
                        name="pspPartnerAvailableOverdraft"
                        placeholder=""
                        title="Remaining overdraft"
                        value={
                          pspPartnerAccountBalanceInfo &&
                          pspPartnerAccountBalanceInfo?.overdraftEnabled
                            ? `${formatBalance(
                                // eslint-disable-next-line no-nested-ternary
                                pspPartnerAccountBalanceInfo?.value < 0
                                  ? (pspPartnerAccountBalanceInfo?.overdraftValue as number) -
                                      Math.abs(
                                        pspPartnerAccountBalanceInfo?.value,
                                      ) <
                                    0
                                    ? 0
                                    : (pspPartnerAccountBalanceInfo?.overdraftValue as number) -
                                      Math.abs(
                                        pspPartnerAccountBalanceInfo?.value,
                                      )
                                  : (pspPartnerAccountBalanceInfo?.overdraftValue as number),
                                currenciesList?.find(
                                  (item) => item.id === currencyId,
                                ),
                              )} ${
                                currencies?.find(
                                  (item) => item.value === currencyId,
                                )?.name
                              }`
                            : '-'
                        }
                      />
                    </div>
                    <div className={styles.overdraftBalanceValue}>
                      <InputText
                        disabled
                        tabIndex={-1}
                        name="pspPartnerOverdraftLimit"
                        placeholder=""
                        title="Overdraft limit"
                        value={
                          pspPartnerAccountBalanceInfo &&
                          pspPartnerAccountBalanceInfo?.overdraftEnabled
                            ? `${formatBalance(
                                pspPartnerAccountBalanceInfo?.overdraftValue as number,
                                currenciesList?.find(
                                  (item) => item.id === currencyId,
                                ),
                              )} ${
                                currencies?.find(
                                  (item) => item.value === currencyId,
                                )?.name
                              }`
                            : '-'
                        }
                      />
                    </div>
                  </div>
                )}
            </>
          )}
          {!control && (
            <>
              {(!overdraftEnabled ||
                (currentPspAccount &&
                  (!currentPspAccount?.account?.overdraftEnabled ||
                    !pspPartnerAccountBalanceInfo?.overdraftEnabled))) && (
                <SidepanelDefaultRow
                  title="Current balance"
                  valueClassName={general ? styles.readCopyButtonWrapper : ''}
                >
                  {pspPartnerAccountBalance !== undefined
                    ? `${formatBalance(pspPartnerAccountBalance)} ${
                        general?.currency?.name
                      }`
                    : '-'}
                  {Boolean(general) && (
                    <button
                      type="button"
                      className={`${styles.readCopyButton} ${
                        copyIsSuccess ? styles.success : ''
                      }`}
                      onClick={() =>
                        onCopy(
                          pspPartnerAccountBalance !== undefined
                            ? pspPartnerAccountBalance?.toString()
                            : '-',
                        )
                      }
                      aria-label="Copy"
                    >
                      <IconCopy />
                    </button>
                  )}
                </SidepanelDefaultRow>
              )}
              {overdraftEnabled &&
                currentPspAccount?.overdraftEnabled &&
                pspPartnerAccountBalanceInfo?.overdraftEnabled && (
                  <>
                    <SidepanelDefaultRow
                      title="Total available balance"
                      valueClassName={
                        general ? styles.readCopyButtonWrapper : ''
                      }
                    >
                      {pspPartnerAccountBalance !== undefined
                        ? `${formatBalance(pspPartnerAccountBalance)} ${
                            general?.currency?.name
                          }`
                        : '-'}
                      {Boolean(general) && (
                        <button
                          type="button"
                          className={`${styles.readCopyButton} ${
                            copyIsSuccess ? styles.success : ''
                          }`}
                          onClick={() =>
                            onCopy(
                              pspPartnerAccountBalance !== undefined
                                ? pspPartnerAccountBalance?.toString()
                                : '-',
                            )
                          }
                          aria-label="Copy"
                        >
                          <IconCopy />
                        </button>
                      )}
                    </SidepanelDefaultRow>
                    <SidepanelDefaultRow title="Remaining overdraft">
                      {pspPartnerAccountBalanceInfo &&
                      pspPartnerAccountBalanceInfo?.overdraftEnabled
                        ? `${formatBalance(
                            // eslint-disable-next-line no-nested-ternary
                            pspPartnerAccountBalanceInfo?.value < 0
                              ? (pspPartnerAccountBalanceInfo?.overdraftValue as number) -
                                  Math.abs(
                                    pspPartnerAccountBalanceInfo?.value,
                                  ) <
                                0
                                ? 0
                                : (pspPartnerAccountBalanceInfo?.overdraftValue as number) -
                                  Math.abs(pspPartnerAccountBalanceInfo?.value)
                              : (pspPartnerAccountBalanceInfo?.overdraftValue as number),
                            currenciesList?.find(
                              (item) => item.id === currencyId,
                            ),
                          )} ${
                            currencies?.find(
                              (item) => item.value === currencyId,
                            )?.name
                          }`
                        : '-'}
                    </SidepanelDefaultRow>
                    <SidepanelDefaultRow title="Overdraft limit">
                      {pspPartnerAccountBalanceInfo &&
                      pspPartnerAccountBalanceInfo?.overdraftEnabled
                        ? `${formatBalance(
                            pspPartnerAccountBalanceInfo?.overdraftValue as number,
                            currenciesList?.find(
                              (item) => item.id === currencyId,
                            ),
                          )} ${
                            currencies?.find(
                              (item) => item.value === currencyId,
                            )?.name
                          }`
                        : '-'}
                    </SidepanelDefaultRow>
                  </>
                )}
            </>
          )}
          {!transactionId && control && (
            <DisableCreationOption
              control={control}
              clearErrors={clearErrors}
            />
          )}
          <SidepanelTextRow
            title="Document #"
            value={enableEdit ? '' : general?.documentNumber}
            name="documentNumber"
            placeholder="Enter Document"
            control={control}
            enableEdit={enableEdit}
            disabled={
              !isEditable ||
              (Boolean(transactionId && general) &&
                baseellaStatus !== BaseellaStatus.READY_TO_PSP)
            }
            tabIndex={
              !isEditable ||
              (Boolean(general) &&
                baseellaStatus !== BaseellaStatus.READY_TO_PSP)
                ? -1
                : 0
            }
            copyEnabled={Boolean(transactionId && general)}
            copyValue={
              enableEdit ? fields?.documentNumber : general?.documentNumber
            }
            error={errors?.documentNumber?.message}
            clearErrors={clearErrors}
          />
        </div>
        <div className={styles.transactionBlockWrapper}>
          <div className={styles.transactionTitle}>Payment details</div>
          <SidepanelTextareaRow
            title="Payment Details"
            value={enableEdit ? '' : general?.info}
            name="info"
            placeholder="Enter Payment Details"
            control={control}
            enableEdit={enableEdit}
            disabled={
              !isEditable ||
              (Boolean(transactionId && general) &&
                baseellaStatus !== BaseellaStatus.READY_TO_PSP)
            }
            tabIndex={
              !isEditable ||
              (Boolean(transactionId && general) &&
                baseellaStatus !== BaseellaStatus.READY_TO_PSP)
                ? -1
                : 0
            }
            error={errors?.info?.message}
            copyEnabled={Boolean(transactionId && general)}
            copyValue={enableEdit ? fields?.info : general?.info}
            fieldValue={infoValue}
            setFieldValue={setInfoValue}
            maxLength={140}
            clearErrors={clearErrors}
          />
        </div>
        {withTransactionUrl && (
          <NavLink
            to={`/transactions/${transactionUrlType}/${transactionId}`}
            target="_blank"
            className={`${styles.transactionLink} ${styles.withMargin}`}
            tabIndex={-1}
          >
            Go to transaction
          </NavLink>
        )}
      </>
    );
  }

  return content;
}

OutgoingGeneralContent.defautProps = {
  withTransactionUrl: false,
  transactionUrlType: '',
  transactionId: '',
  activePermission: undefined,
  amount: undefined,
  setAmount: undefined,
  balances: undefined,
  accountBalancesIsLoading: false,
  fields: undefined,
  overdraftEnabled: false,
  pspPartnersAccountsList: [],
  pspPartnerAccountBalanceInfo: undefined,
};
